import { useCallback, useMemo, useState } from 'react';
import type { StepperItemProps } from '@components/Stepper';

import { EndorsementStep } from '../types';

interface UseStepperReturn {
  currentStep: EndorsementStep;
  getStepperItemState: (visualStepIndex: number) => StepperItemProps;
  onNext: () => void;
  onPrevious: () => void;
  totalSteps: number;
}

interface UseStepperArgs {
  isStandalone: boolean;
  isDuplicatingEndorsement: boolean;
  onClose?: () => void;
}

export function useStepper({ isStandalone, isDuplicatingEndorsement, onClose }: UseStepperArgs): UseStepperReturn {
  const initialStep = isStandalone ? EndorsementStep.ParentSelection : EndorsementStep.SummaryTemplate;
  const [currentStep, setCurrentStep] = useState<EndorsementStep>(initialStep);

  // The visible steps for this session, in order. EndorsementSelection only appears
  // when duplicating an existing endorsement; ParentSelection only when standalone.
  const stepSequence = useMemo(() => {
    const steps: EndorsementStep[] = [];
    if (isStandalone) steps.push(EndorsementStep.ParentSelection);
    steps.push(EndorsementStep.SummaryTemplate);
    if (isDuplicatingEndorsement) steps.push(EndorsementStep.EndorsementSelection);
    steps.push(EndorsementStep.BasicInfo, EndorsementStep.ReasonPremium);
    return steps;
  }, [isStandalone, isDuplicatingEndorsement]);

  const getStepperItemState = useCallback(
    (visualStepIndex: number): StepperItemProps => {
      const step = stepSequence[visualStepIndex - 1];
      const currentPosition = stepSequence.indexOf(currentStep);

      if (step === currentStep) {
        return { isActive: true };
      }

      if (visualStepIndex - 1 < currentPosition) {
        return { isCompleted: true };
      }

      return {};
    },
    [currentStep, stepSequence],
  );

  const onPrevious = useCallback(() => {
    setCurrentStep((current) => {
      if (
        current === EndorsementStep.ParentSelection ||
        (!isStandalone && current === EndorsementStep.SummaryTemplate)
      ) {
        onClose?.();
        return current;
      }

      if (current === EndorsementStep.EndorsementSelection) {
        return EndorsementStep.SummaryTemplate;
      }

      if (current === EndorsementStep.BasicInfo) {
        return isDuplicatingEndorsement ? EndorsementStep.EndorsementSelection : EndorsementStep.SummaryTemplate;
      }

      if (current === EndorsementStep.ReasonPremium) {
        return EndorsementStep.BasicInfo;
      }

      return current - 1;
    });
  }, [isStandalone, isDuplicatingEndorsement, onClose]);

  const onNext = useCallback(() => {
    setCurrentStep((current) => {
      if (current === EndorsementStep.SummaryTemplate && isDuplicatingEndorsement) {
        return EndorsementStep.EndorsementSelection;
      }

      if (current === EndorsementStep.EndorsementSelection || current === EndorsementStep.SummaryTemplate) {
        return EndorsementStep.BasicInfo;
      }

      return current < EndorsementStep.ReasonPremium ? current + 1 : current;
    });
  }, [isDuplicatingEndorsement]);

  return {
    currentStep,
    getStepperItemState,
    onNext,
    onPrevious,
    totalSteps: stepSequence.length,
  };
}
